import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Calendar, MapPin, Users, TrendingUp, Eye, Plus, Loader2, Share2, Upload, Edit, Filter, Download, Trash2, Copy, CheckSquare, AlertCircle, CreditCard, CheckCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import ShareEventModal from '../components/ShareEventModal';
import EditEventModal from '../components/EditEventModal';
import CopyEventModal from '../components/CopyEventModal';
import ContributionApprovalSection from '../components/ContributionApprovalSection';
import PlanUpgradeModal from '../components/PlanUpgradeModal';
import EditRSVPModal from '../components/EditRSVPModal';
import CoHostManagementModal from '../components/CoHostManagementModal';

const HostDashboard = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [joinedEvents, setJoinedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [rsvpStats, setRsvpStats] = useState(null);
  const [shareEvent, setShareEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [copyEvent, setCopyEvent] = useState(null);
  const [upgradeEvent, setUpgradeEvent] = useState(null);
  const [editRSVP, setEditRSVP] = useState(null);
  const [editRSVPEvent, setEditRSVPEvent] = useState(null);
  const [coHostManageEvent, setCoHostManageEvent] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [joinedDateFilter, setJoinedDateFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('created');
  const [expandedEventId, setExpandedEventId] = useState(null);

  // Fetch both datasets on initial load
  useEffect(() => {
    fetchMyEvents();
    fetchJoinedEvents();
  }, []);

  // Refetch created events when date filter changes
  useEffect(() => {
    if (activeTab === 'created') {
      fetchMyEvents();
    }
  }, [dateFilter]);

  // Refetch joined events when date filter changes
  useEffect(() => {
    if (activeTab === 'joined') {
      fetchJoinedEvents();
    }
  }, [joinedDateFilter]);

  const getDateRange = (filter) => {
    const now = new Date();
    let dateFrom = null;
    let dateTo = null;

    switch (filter) {
      case 'last_6_months':
        dateFrom = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        dateTo = now;
        break;
      case 'this_year':
        dateFrom = new Date(now.getFullYear(), 0, 1);
        dateTo = now;
        break;
      case 'last_year':
        dateFrom = new Date(now.getFullYear() - 1, 0, 1);
        dateTo = new Date(now.getFullYear() - 1, 11, 31);
        break;
      case 'all':
      default:
        return { dateFrom: null, dateTo: null };
    }

    return {
      dateFrom: dateFrom.toISOString().split('T')[0],
      dateTo: dateTo.toISOString().split('T')[0]
    };
  };

  // Group events by month and year
  const groupEventsByMonth = (eventsList) => {
    const grouped = {};
    eventsList.forEach(event => {
      const date = new Date(event.created_at || event.date);
      const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(event);
    });
    // Sort by date (newest first)
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date));
    });
    return grouped;
  };

  const fetchMyEvents = async () => {
    setLoading(true);
    try {
      const { dateFrom, dateTo } = getDateRange(dateFilter);
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await axios.get(`${API}/events/my-events/list?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Sort events by created_at (latest first)
      const sortedEvents = response.data.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setEvents(sortedEvents);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchJoinedEvents = async () => {
    setLoading(true);
    try {
      // Fetch user's RSVPs
      const rsvpResponse = await axios.get(`${API}/rsvps/my-rsvps`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Create a map of event_id to RSVP
      const rsvpMap = {};
      rsvpResponse.data.forEach(rsvp => {
        rsvpMap[rsvp.event_id] = rsvp;
      });

      // Get unique event IDs from RSVPs
      const eventIds = [...new Set(rsvpResponse.data.map(rsvp => rsvp.event_id))];

      // Fetch event details for each joined event
      const eventPromises = eventIds.map(eventId =>
        axios.get(`${API}/events/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      );

      const eventResponses = await Promise.all(eventPromises);
      let joinedEventsData = eventResponses
        .filter(res => res !== null)
        .map(res => {
          const event = res.data;
          // Attach the RSVP data to the event
          event.myRsvp = rsvpMap[event.id];
          return event;
        });

      // Apply date filter
      const { dateFrom, dateTo } = getDateRange(joinedDateFilter);
      if (dateFrom && dateTo) {
        joinedEventsData = joinedEventsData.filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= new Date(dateFrom) && eventDate <= new Date(dateTo);
        });
      }

      // Sort by created_at (latest first)
      const sortedJoinedEvents = joinedEventsData.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setJoinedEvents(sortedJoinedEvents);
    } catch (error) {
      toast.error('Failed to load joined events');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventGuests = async (eventId) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/guests`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const guestsData = response.data;
      setGuests(guestsData);
      
      // Fetch RSVP stats
      const statsResponse = await axios.get(`${API}/events/${eventId}/rsvp-stats`);
      const stats = statsResponse.data;
      
      // Calculate total guest count from all RSVPs
      const totalGuestCount = guestsData.reduce((sum, guest) => sum + (guest.total_guests || 0), 0);
      stats.total_guest_count = totalGuestCount;
      
      setRsvpStats(stats);
    } catch (error) {
      toast.error('Failed to load guests');
    }
  };

  const handleApprovals = (event) => {
    navigate(`/approvals/${event.id}`);
  };


  const handleDownloadContributionTracker = async (eventId, eventTitle) => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/contribution-tracker`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = response.data.data;
      const eventData = response.data;
      
      if (data.length === 0) {
        toast.info('No contributions to download yet');
        return;
      }

      // Get all unique goal names from all contributions
      const allGoals = new Set();
      data.forEach(row => {
        Object.keys(row.goal_contributions || {}).forEach(goal => allGoals.add(goal));
      });
      const goalNames = Array.from(allGoals);

      // Create CSV content with dynamic goal columns
      const headers = [
        'Event ID',
        'Guest Name', 
        'Email ID', 
        ...goalNames.map(goal => `${goal} (₹)`),
        'Total Amount Contributed (₹)',
        'Transaction ID',
        'Transaction Date',
        'Payment Status',
        'Amount Approved By',
        'Role of Approver',
        'RSVP Option',
        'Total Guests Count',
        'Adults - Male',
        'Adults - Female',
        'Kids - Male',
        'Kids - Female'
      ];
      
      const csvRows = [
        headers.join(','),
        ...data.map(row => {
          const goalAmounts = goalNames.map(goal => row.goal_contributions[goal] || 0);
          return [
            `"${eventData.event_code || eventId}"`,
            `"${row.guest_name}"`,
            `"${row.guest_email}"`,
            ...goalAmounts,
            row.total_amount_contributed,
            `"${row.transaction_id || 'N/A'}"`,
            row.transaction_date || 'N/A',
            row.payment_status || 'Pending',
            `"${row.amount_approved_by || 'N/A'}"`,
            row.role_of_approver || 'N/A',
            row.rsvp_status,
            row.total_guests,
            row.adults_male,
            row.adults_female,
            row.kids_male,
            row.kids_female
          ].join(',');
        })
      ];

      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${eventTitle.replace(/[^a-z0-9]/gi, '_')}_Contribution_Tracker.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Contribution tracker downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download contribution tracker');
      console.error(error);
    }
  };


  // Check if RSVP editing is allowed (1 hour before event)
  const canEditRSVP = (event) => {
    const eventDateTime = new Date(`${event.date}T${event.time}`);
    const now = new Date();
    const hoursUntilEvent = (eventDateTime - now) / (1000 * 60 * 60);
    return hoursUntilEvent > 1;
  };

  // Handle Edit RSVP click
  const handleEditRSVP = (event) => {
    if (!canEditRSVP(event)) {
      toast.error('RSVP editing is not allowed within 1 hour of the event start time.');
      return;
    }

    // Use the RSVP data already attached to the event
    if (event.myRsvp) {
      setEditRSVP(event.myRsvp);
      setEditRSVPEvent(event);
    } else {
      toast.error('RSVP data not found. Please refresh the page.');
    }
  };

  const handleDeleteEvent = async (eventId, eventTitle) => {
    const confirmMessage = `Do you want to delete the event "${eventTitle}"?\n\nNote: Once you delete the event, you cannot retrieve the data again.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      console.log('Deleting event:', eventId);
      const response = await axios.delete(`${API}/events/${eventId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Delete response:', response.data);
      toast.success('Event deleted successfully');
      
      // Refresh the list after a short delay
      setTimeout(() => {
        fetchMyEvents();
      }, 500);
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete event';
      toast.error(errorMessage);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-contribo-navy" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy from-opacity-5 via-contribo-gold via-opacity-5 to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Event Dashboard</h1>
              <p className="text-white mt-2">Manage your events and track contributions</p>
            </div>
            {/* Removed Create New Event button - access via Host/Co-host Dashboard */}
          </div>
        </div>

        {/* Event Type Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
          <Card 
            className={`glass-card cursor-pointer transition-all hover:shadow-xl ${activeTab === 'created' ? 'border-2 border-contribo-navy' : ''}`}
            onClick={() => setActiveTab('created')}
          >
            <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Events Created</p>
                  <p className="text-2xl md:text-4xl font-bold text-contribo-navy">
                    {events.length}
                  </p>
                </div>
                <div className="bg-contribo-navy bg-opacity-10 p-2 md:p-4 rounded-full">
                  <Calendar className="h-6 w-6 md:h-10 md:w-10 text-contribo-navy" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`glass-card cursor-pointer transition-all hover:shadow-xl ${activeTab === 'joined' ? 'border-2 border-contribo-gold' : ''}`}
            onClick={() => setActiveTab('joined')}
          >
            <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Events Joined</p>
                  <p className="text-2xl md:text-4xl font-bold text-contribo-gold">
                    {joinedEvents.length}
                  </p>
                </div>
                <div className="bg-contribo-gold bg-opacity-10 p-2 md:p-4 rounded-full">
                  <Users className="h-6 w-6 md:h-10 md:w-10 text-contribo-gold" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`glass-card cursor-pointer transition-all hover:shadow-xl ${activeTab === 'approvals' ? 'border-2 border-green-600' : ''}`}
            onClick={() => setActiveTab('approvals')}
          >
            <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Goal Contributions</p>
                  <p className="text-2xl md:text-4xl font-bold text-green-600">
                    {/* Will show count dynamically */}
                    <CheckSquare className="h-6 w-6 md:h-10 md:w-10" />
                  </p>
                </div>
                <div className="bg-green-100 p-2 md:p-4 rounded-full">
                  <CheckSquare className="h-6 w-6 md:h-10 md:w-10 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1 md:mt-2">Approve contributions to your UPI</p>
            </CardContent>
          </Card>
        </div>

        {/* Additional Metrics for Created Events */}
        {activeTab === 'created' && events.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-green-700 font-semibold mb-1">Approved</p>
                    <p className="text-xl md:text-3xl font-bold text-green-900">
                      {events.filter(e => e.approval_status === 'approved').length}
                    </p>
                  </div>
                  <div className="bg-green-200 p-1.5 md:p-3 rounded-full">
                    <CheckSquare className="h-4 w-4 md:h-6 md:w-6 text-green-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
              <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-yellow-700 font-semibold mb-1">Pending</p>
                    <p className="text-xl md:text-3xl font-bold text-yellow-900">
                      {events.filter(e => e.approval_status === 'pending').length}
                    </p>
                  </div>
                  <div className="bg-yellow-200 p-1.5 md:p-3 rounded-full">
                    <AlertCircle className="h-4 w-4 md:h-6 md:w-6 text-yellow-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-purple-700 font-semibold mb-1">Invested</p>
                    <p className="text-sm md:text-2xl font-bold text-purple-900 truncate">
                      ₹{events.reduce((sum, e) => {
                        const totalBudget = e.contribution_goals.reduce((s, g) => s + g.target_amount, 0);
                        let fee = 49;
                        if (totalBudget > 20000) fee = 99;
                        if (totalBudget > 100000) fee = 199;
                        if (totalBudget > 500000) fee = 399;
                        return sum + (e.approval_status === 'approved' ? fee : 0);
                      }, 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-purple-600 mt-1 hidden md:block">Plan fees paid</p>
                  </div>
                  <div className="bg-purple-200 p-1.5 md:p-3 rounded-full flex-shrink-0">
                    <TrendingUp className="h-4 w-4 md:h-6 md:w-6 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-3 md:pt-6 pb-3 md:pb-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-blue-700 font-semibold mb-1">Raised</p>
                    <p className="text-sm md:text-2xl font-bold text-blue-900 truncate">
                      ₹{events.reduce((sum, e) => sum + (e.total_contributions || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-blue-600 mt-1 hidden md:block">Across all events</p>
                  </div>
                  <div className="bg-blue-200 p-1.5 md:p-3 rounded-full flex-shrink-0">
                    <CreditCard className="h-4 w-4 md:h-6 md:w-6 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Date Range Filter - Only show for created/joined tabs */}
        {activeTab !== 'approvals' && (
          <div className="mb-6">
            <Card className="glass-card p-4">
              <div className="flex items-center gap-4">
                <Filter className="h-5 w-5 text-contribo-navy" />
                <span className="text-sm font-semibold text-gray-700">Filter Events:</span>
                <Select 
                  value={activeTab === 'created' ? dateFilter : joinedDateFilter} 
                  onValueChange={(value) => activeTab === 'created' ? setDateFilter(value) : setJoinedDateFilter(value)}
                >
                  <SelectTrigger className="w-[200px]" data-testid="date-filter-select">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="filter-all">All Events</SelectItem>
                    <SelectItem value="last_6_months" data-testid="filter-6months">Last 6 Months</SelectItem>
                    <SelectItem value="this_year" data-testid="filter-this-year">This Year</SelectItem>
                    <SelectItem value="last_year" data-testid="filter-last-year">Last Year</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-500">
                  {activeTab === 'created' ? events.length : joinedEvents.length} {((activeTab === 'created' ? events.length : joinedEvents.length) === 1) ? 'event' : 'events'} found
                </span>
              </div>
            </Card>
          </div>
        )}

        {/* Approvals Tab */}
        {activeTab === 'approvals' ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Goal Contribution Approvals</h2>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>📋 Approve contributions received in your UPI account:</strong> Contributors first pay commission to Contribo (verified by admin), then pay goal contributions to you. Verify transaction IDs against your bank statement.
              </p>
            </div>
            {events.length === 0 ? (
              <Card className="glass-card p-12 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No events yet</h3>
                <p className="text-gray-600 mb-6">Create your first event to see contribution approvals</p>
                <Button
                  className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
                  onClick={() => navigate('/create-event')}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Event
                </Button>
              </Card>
            ) : (
              <div className="space-y-8">
                {events.map((event) => (
                  <div key={event.id}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                        <p className="text-sm text-gray-600">
                          {new Date(event.date).toLocaleDateString()} • {event.location}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/event/${event.id}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Event
                      </Button>
                    </div>
                    <ContributionApprovalSection eventId={event.id} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'created' && events.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No events yet</h3>
            <p className="text-gray-600 mb-6">Create your first event to get started</p>
            <Button
              className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
              onClick={() => navigate('/create-event')}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Event
            </Button>
          </Card>
        ) : activeTab === 'joined' && joinedEvents.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No joined events yet</h3>
            <p className="text-gray-600 mb-6">Join an event to see it here</p>
          </Card>
        ) : activeTab !== 'approvals' ? (
          <div className="space-y-6">
            {/* Group events by month/year */}
            {Object.entries(groupEventsByMonth(activeTab === 'created' ? events : joinedEvents)).map(([monthYear, monthEvents]) => (
              <div key={monthYear}>
                {/* Month/Year Header */}
                <h3 className="text-lg font-bold text-contribo-navy mb-3 sticky top-0 bg-gray-50 py-2 z-10">{monthYear}</h3>
                
                {/* Events Grid - 3 per row for 360px */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {monthEvents.map((event) => {
                    const isExpanded = expandedEventId === event.id;

                    return (
                      <React.Fragment key={event.id}>
                        {/* Event Tile - Compact */}
                        <Card
                          className={`cursor-pointer hover:shadow-lg transition-all ${event.approval_status === 'pending' ? 'border-2 border-yellow-400' : 'border-2 border-contribo-navy'} ${isExpanded ? 'shadow-xl' : ''}`}
                          onClick={() => setExpandedEventId(isExpanded ? null : event.id)}
                        >
                          <CardContent className="p-2 text-center">
                            <div className="text-[10px] font-bold text-contribo-navy mb-1 truncate">{event.title}</div>
                            <div className="text-[9px] text-gray-600 mb-1">
                              <div className="font-semibold">{event.event_code}</div>
                              <div>By: {event.host_name}</div>
                              <div>{new Date(event.created_at || event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                            </div>
                            <Badge className={`text-[8px] px-1 py-0 ${event.approval_status === 'pending' ? 'bg-yellow-500' : 'bg-green-500'}`}>
                              {event.approval_status === 'pending' ? 'Pending' : 'Active'}
                            </Badge>
                          </CardContent>
                        </Card>

                        {/* Expanded Options - Show below tile when clicked */}
                        {isExpanded && (
                          <div className="col-span-3 mt-2">
                            <div className="w-full max-w-[360px] mx-auto">
                              <Card className="border-2 border-contribo-gold shadow-xl overflow-hidden">
                                <CardContent className="p-2 overflow-hidden">
                                {/* Header with close */}
                                <div className="flex items-center justify-between mb-2 pb-2 border-b">
                                  <div className="flex-1">
                                    <div className="text-[9px] text-gray-600">
                                      <span className="font-semibold">Event Name:</span> <span className="text-contribo-navy font-bold">{event.title}</span>
                                    </div>
                                    <div className="text-[9px] text-gray-600 mt-0.5">
                                      <span className="font-semibold">Event ID:</span> <span className="text-gray-800">{event.event_code}</span>
                                    </div>
                                  </div>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedEventId(null);
                                    }} 
                                    className="text-gray-500 hover:text-gray-700 text-lg px-2"
                                  >
                                    ✕
                                  </button>
                                </div>
                                
                                {/* Compact Event Info */}
                                <div className="grid grid-cols-2 gap-1 text-[9px] mb-2 bg-gray-50 p-2 rounded">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-2 w-2 text-contribo-navy" />
                                    <span>{new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <MapPin className="h-2 w-2 text-contribo-navy" />
                                    <span className="truncate">{event.location}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Users className="h-2 w-2 text-contribo-navy" />
                                    <span>{event.total_rsvps} RSVPs</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <TrendingUp className="h-2 w-2 text-contribo-navy" />
                                    <span>₹{event.total_contributions || 0}</span>
                                  </div>
                                </div>

                                {/* Action Tiles - 3 per row */}
                                <div className="grid grid-cols-3 gap-1.5">
                                  {/* Approvals Tile */}
                                  <Card
                                    className="cursor-pointer hover:shadow-md transition-shadow border border-yellow-300 bg-yellow-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/approvals/${event.id}`);
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <CheckCircle className="h-4 w-4 mx-auto mb-0.5 text-yellow-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Approvals</p>
                                    </CardContent>
                                  </Card>

                                  {/* Photos Tile */}
                                  <Card
                                    className="cursor-pointer hover:shadow-md transition-shadow border border-purple-300 bg-purple-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/event/${event.id}/photos`);
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <Download className="h-4 w-4 mx-auto mb-0.5 text-purple-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Photos</p>
                                    </CardContent>
                                  </Card>

                                  {/* Share Tile */}
                                  <Card
                                    className={`cursor-pointer hover:shadow-md transition-shadow border border-green-300 bg-green-50 ${event.approval_status === 'pending' ? 'opacity-50' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (event.approval_status !== 'pending') {
                                        setShareEvent(event);
                                      }
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <Share2 className="h-4 w-4 mx-auto mb-0.5 text-green-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Share</p>
                                    </CardContent>
                                  </Card>

                                  {/* Edit Event Tile */}
                                  <Card
                                    className="cursor-pointer hover:shadow-md transition-shadow border border-purple-300 bg-purple-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditEvent(event);
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <Edit className="h-4 w-4 mx-auto mb-0.5 text-purple-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Edit Event</p>
                                    </CardContent>
                                  </Card>

                                  {/* Upgrade Plan Tile */}
                                  <Card
                                    className="cursor-pointer hover:shadow-md transition-shadow border border-orange-300 bg-orange-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setUpgradeEvent(event);
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <TrendingUp className="h-4 w-4 mx-auto mb-0.5 text-orange-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Upgrade Plan</p>
                                    </CardContent>
                                  </Card>

                                  {/* Copy Event Tile */}
                                  <Card
                                    className="cursor-pointer hover:shadow-md transition-shadow border border-pink-300 bg-pink-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCopyEvent(event);
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <Copy className="h-4 w-4 mx-auto mb-0.5 text-pink-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Copy Event</p>
                                    </CardContent>
                                  </Card>

                                  {/* Co-hosts Tile */}
                                  <Card
                                    className="cursor-pointer hover:shadow-md transition-shadow border border-indigo-300 bg-indigo-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCoHostManageEvent(event);
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <Users className="h-4 w-4 mx-auto mb-0.5 text-indigo-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Co-hosts</p>
                                    </CardContent>
                                  </Card>

                                  {/* Download Tracker Tile */}
                                  <Card
                                    className="cursor-pointer hover:shadow-md transition-shadow border border-teal-300 bg-teal-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadContributionTracker(event.id, event.title);
                                    }}
                                  >
                                    <CardContent className="p-2 text-center">
                                      <Download className="h-4 w-4 mx-auto mb-0.5 text-teal-600" />
                                      <p className="text-[9px] font-semibold text-gray-700">Tracker</p>
                                    </CardContent>
                                  </Card>

                                  {/* Delete Event Tile - Only for Event Creator */}
                                  {event.host_id === user?.id && (
                                    <Card
                                      className="cursor-pointer hover:shadow-md transition-shadow border border-red-300 bg-red-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteEvent(event.id, event.title);
                                      }}
                                    >
                                      <CardContent className="p-2 text-center">
                                        <Trash2 className="h-4 w-4 mx-auto mb-0.5 text-red-600" />
                                        <p className="text-[9px] font-semibold text-gray-700">Delete Event</p>
                                      </CardContent>
                                    </Card>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Approvals tab handled above in the main event loop */}

        {/* Event Details Section - Only for created events */}
        {activeTab === 'created' && selectedEvent && (
          <div data-testid="event-details-view" className="mt-6">
                <div className="space-y-6">
                  {/* Event Info */}
                  <Card className="glass-card">
                    <CardHeader>
                      <CardTitle className="text-2xl">{selectedEvent.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{selectedEvent.description}</p>
                      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="bg-contribo-navy bg-opacity-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total RSVPs</p>
                          <p className="text-3xl font-bold text-contribo-navy">
                            {selectedEvent.total_rsvps}
                          </p>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Guests</p>
                          <p className="text-3xl font-bold text-orange-600">
                            {rsvpStats?.total_guest_count || 0}
                          </p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Total Raised</p>
                          <p className="text-3xl font-bold text-green-600">
                            ₹{selectedEvent.total_contributions.toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">Goals</p>
                          <p className="text-3xl font-bold text-blue-600">
                            {selectedEvent.contribution_goals.length}
                          </p>
                        </div>
                        {rsvpStats && (
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-2">RSVP Breakdown</p>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-green-700">✓ Coming:</span>
                                <span className="font-bold text-green-700">{rsvpStats.yes}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-yellow-700">? Maybe:</span>
                                <span className="font-bold text-yellow-700">{rsvpStats.maybe}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-red-700">✗ Not Coming:</span>
                                <span className="font-bold text-red-700">{rsvpStats.no}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Contribution Goals Progress */}
                  <Card className="glass-card">
                    <CardHeader>
                      <CardTitle>Contribution Goals Progress</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedEvent.contribution_goals.map((goal) => {
                        const progress = (goal.current_amount / goal.target_amount) * 100;
                        return (
                          <div key={goal.id} className="p-4 border-2 border-purple-100 rounded-lg">
                            <h4 className="font-semibold text-gray-900 mb-2">{goal.title}</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  ₹{goal.current_amount.toLocaleString()}
                                </span>
                                <span className="text-gray-600">
                                  ₹{goal.target_amount.toLocaleString()}
                                </span>
                              </div>
                              <Progress value={progress} className="h-2" />
                              <p className="text-sm font-semibold text-contribo-navy">
                                {progress.toFixed(1)}% Complete
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  {/* Uploaded Guest List */}
                  {selectedEvent.guest_list && selectedEvent.guest_list.length > 0 && (
                    <Card className="glass-card">
                      <CardHeader>
                        <CardTitle>Uploaded Guest List ({selectedEvent.guest_list.length})</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                          {selectedEvent.guest_list.map((guest, index) => (
                            <div
                              key={index}
                              className="p-3 bg-green-50 rounded-lg border border-green-200"
                              data-testid={`uploaded-guest-${index}`}
                            >
                              <p className="font-semibold text-gray-900">{guest.name}</p>
                              <p className="text-sm text-gray-600">{guest.mobile}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Guest Contributions */}
                  <Card className="glass-card">
                    <CardHeader>
                      <CardTitle>Guest Contributions ({guests.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {guests.length === 0 ? (
                        <p className="text-gray-600 text-center py-6">No RSVPs yet</p>
                      ) : (
                        <div className="space-y-3">
                          {guests.map((guest) => {
                            const statusColors = {
                              yes: 'bg-green-100 text-green-700',
                              no: 'bg-red-100 text-red-700',
                              maybe: 'bg-yellow-100 text-yellow-700'
                            };
                            const statusLabels = {
                              yes: '✓ Coming',
                              no: '✗ Not Coming',
                              maybe: '? Maybe'
                            };

                            return (
                              <div
                                key={guest.id}
                                className="flex justify-between items-start p-4 bg-white rounded-lg border border-purple-100"
                                data-testid={`guest-${guest.id}`}
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-semibold text-gray-900">{guest.guest_name}</p>
                                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${statusColors[guest.rsvp_status]}`}>
                                      {statusLabels[guest.rsvp_status]}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600">{guest.guest_mobile}</p>
                                  {guest.contribution_goal_title && (
                                    <p className="text-sm text-contribo-navy mt-1">
                                      Contributed to: {guest.contribution_goal_title}
                                    </p>
                                  )}
                                  {guest.total_guests > 0 && (
                                    <div className="text-xs text-gray-600 mt-2 bg-blue-50 p-2 rounded inline-block">
                                      <span className="font-semibold">Guests: {guest.total_guests}</span>
                                      <span className="ml-2">
                                        (M: {guest.adults_male + guest.kids_male}, F: {guest.adults_female + guest.kids_female})
                                      </span>
                                    </div>
                                  )}
                                  {guest.message && (
                                    <p className="text-sm text-gray-500 italic mt-2">
                                      "{guest.message}"
                                    </p>
                                  )}
                                </div>
                                {guest.contribution_amount && guest.contribution_amount > 0 && (
                                  <div className="text-right ml-4">
                                    <p className="text-lg font-bold text-green-600">
                                      ₹{guest.contribution_amount.toLocaleString()}
                                    </p>
                                    <span
                                      className={`text-xs px-2 py-1 rounded-full ${
                                        guest.payment_status === 'completed'
                                          ? 'bg-green-100 text-green-700'
                                          : guest.payment_status === 'pending'
                                          ? 'bg-yellow-100 text-yellow-700'
                                          : 'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {guest.payment_status}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
            </div>
          </div>
        )}
      </div>

      {/* Share Event Modal */}
      {shareEvent && (
        <ShareEventModal
          event={shareEvent}
          onClose={() => setShareEvent(null)}
        />
      )}

      {/* Edit Event Modal */}
      {editEvent && (
        <EditEventModal
          event={editEvent}
          onClose={() => setEditEvent(null)}
          onSuccess={() => {
            setEditEvent(null);
            fetchMyEvents();
            if (selectedEvent && selectedEvent.id === editEvent.id) {
              setSelectedEvent(null);
            }
          }}
        />
      )}

      {/* Copy Event Modal */}
      {copyEvent && (
        <CopyEventModal
          event={copyEvent}
          onClose={() => setCopyEvent(null)}
        />
      )}

      {/* Upgrade Plan Modal */}
      {upgradeEvent && (
        <PlanUpgradeModal
          isOpen={!!upgradeEvent}
          onClose={() => setUpgradeEvent(null)}
          event={upgradeEvent}
          token={token}
          onUpgradeSuccess={() => {
            setUpgradeEvent(null);
            fetchMyEvents();
          }}
        />
      )}

      {/* Co-host Management Modal */}
      {coHostManageEvent && (
        <CoHostManagementModal
          isOpen={!!coHostManageEvent}
          onClose={() => setCoHostManageEvent(null)}
          eventId={coHostManageEvent.id}
          isHost={coHostManageEvent.host_id === user?.id}
        />
      )}

      {/* Edit RSVP Modal */}
      {editRSVP && editRSVPEvent && (
        <EditRSVPModal
          rsvp={editRSVP}
          event={editRSVPEvent}
          onClose={() => {
            setEditRSVP(null);
            setEditRSVPEvent(null);
          }}
          onSuccess={() => {
            setEditRSVP(null);
            setEditRSVPEvent(null);
            fetchJoinedEvents();
            toast.success('Your RSVP has been updated successfully!');
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default HostDashboard;
